import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchPageType, useStateQueryFields} from '../search-hooks';
import {useTranslation} from 'react-i18next';
import {setSearchQueryFieldsAction} from '../duck/search-actions';
import Dropdown from 'react-bootstrap/Dropdown';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import classNames from 'clsx';
import {buildDefaultFieldTitle} from '../../utils/utils';

interface Option {
    value: string;
    label: string;
    isSelected: boolean;
}

export const SearchQueryFieldsSelection = React.memo(() => {
    const showFieldSpecificSearch = useSelector((state) => state.settings.fieldSpecificSearch);
    if (!showFieldSpecificSearch) {
        return null;
    }
    return <SearchQueryFieldsSelectionComponent />;
});

export const buildTranslationKey = (queryField: string): string => {
    return 'fieldTitle.' + queryField.split('.', 2).join('.') + '.label';
};

const SearchQueryFieldsSelectionComponent = React.memo(() => {
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const {t} = useTranslation();

    const containerName = useSelector((state) => state.settings.containerName);
    const allQueryFields = useSelector((state) => state.settings.containers[containerName]?.queryFields);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedQueryFields = useStateQueryFields() || [];
    const allFieldsSelected = selectedQueryFields.length === allQueryFields?.length;
    const [show, setShow] = useState<boolean>(false);

    const renderQueryFieldLabel = useCallback((queryField: string) => {
        return t(buildTranslationKey(queryField), buildDefaultFieldTitle(queryField));
    }, [t]);

    const options: Option[] = useMemo(() => {
        return (
            allQueryFields?.map((queryField) => ({
                value: queryField,
                label: renderQueryFieldLabel(queryField),
                isSelected: selectedQueryFields.indexOf(queryField) >= 0,
            })) || []
        );
    }, [renderQueryFieldLabel, selectedQueryFields, allQueryFields]);

    function getValueBasedOnSelection() {
        if (!selectedQueryFields.length) {
            return t('fieldTitle.none.label');
        }
        if (selectedQueryFields.length === 1) {
            return renderQueryFieldLabel(selectedQueryFields[0]);
        }
        if (selectedQueryFields.length === allQueryFields?.length) {
            return t(`fieldTitle.allLong.label`);
        }
        return t('fieldTitle.multiple.label');
    }

    function changeQueryFieldSelection(toggledValue: string, isSelected: boolean) {
        let newSelected: string[] = [...selectedQueryFields];
        if (isSelected) {
            newSelected = newSelected.filter((val) => val !== toggledValue);
        } else {
            newSelected.push(toggledValue);
        }
        dispatch(setSearchQueryFieldsAction(searchPageType, newSelected));
    }

    function selectAllFields() {
        if (allFieldsSelected) {
            dispatch(setSearchQueryFieldsAction(searchPageType, []));
        } else {
            dispatch(setSearchQueryFieldsAction(searchPageType, allQueryFields));
        }
    }

    function toggleMenuVisibility(o, e, {source}) {
        if (source !== 'select') {
            setShow(!show);
        }
    }

    return (
        <Dropdown onToggle={toggleMenuVisibility} show={show} id={'search-query-field-dropdown'}>
            <Dropdown.Toggle id={'search-query-field-toggle'}>
                {getValueBasedOnSelection()}
                <SvgIcon icon={Icons.CHEVRON_DOWN} id={'icon-down'} />
            </Dropdown.Toggle>
            <Dropdown.Menu id={'search-query-field-menu'} alignRight>
                <Dropdown.Item onClick={selectAllFields}>
                    <SvgIcon
                        icon={Icons.CHECKMARK}
                        className={classNames('icon-checkmark', allFieldsSelected ? 'selected' : 'unselected')}
                    />
                    {t('fieldTitle.allLong.label')}
                </Dropdown.Item>
                <Dropdown.Divider />
                {options?.map(({value, label, isSelected}) => (
                    <Dropdown.Item key={value} onClick={() => changeQueryFieldSelection(value, isSelected)}>
                        <SvgIcon
                            icon={Icons.CHECKMARK}
                            className={classNames('icon-checkmark', isSelected ? 'selected' : 'unselected')}
                        />
                        {label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
});
